// Copyright 2017-2025 @polkadot/apps-config authors & contributors
// SPDX-License-Identifier: Apache-2.0

import DUNIPOD_CONFIG from '../../../../config/dunipod.json' with { type: 'json' };
import DUNITER_CONFIG from '../../../../config/duniter.json' with { type: 'json' };

interface NetworkConfig {
  genesisHash: `0x${string}`;
  providers: Record<string, `wss://${string}`>;
  dunipod: string[];
}

const configCache = new Map<string, Promise<NetworkConfig>>();

function localDunipodEndpoints (networkName: string): string[] {
  const conf = (DUNIPOD_CONFIG as Record<string, { fallbackEndpoints?: string[] } | undefined>)[networkName];

  return conf?.fallbackEndpoints ?? [];
}

async function fetchAndCache (networkName: string): Promise<NetworkConfig> {
  const url = `https://git.duniter.org/nodes/networks/-/raw/master/${networkName}.json`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch config from ${url}`);
    }

    const data = await response.json();

    console.log(`Successfully fetched remote config for ${networkName}`);
    // The remote file has 'rpc' and 'genesis_hash'. It also has 'dunipod' on
    // g1 and gtest — but not on gdev, where no pod is deployed and the field is
    // simply absent. So the field is authoritative when present and non-empty,
    // and the bundled list in config/dunipod.json only covers the networks the
    // registry does not describe yet.
    const remoteDunipod: string[] = Array.isArray(data.dunipod) ? data.dunipod : [];
    const config = {
      dunipod: remoteDunipod.length > 0 ? remoteDunipod : localDunipodEndpoints(networkName),
      genesisHash: data.genesis_hash,
      providers: data.rpc.reduce((acc: Record<string, any>, url: string) => {
        // We need provider names, remote file doesn't have them.
        // We'll generate them for now.
        const name = new URL(url).hostname;

        acc[name] = url;

        return acc;
      }, {})
    };

    localStorage.setItem(`networkConfig-${networkName}`, JSON.stringify(config));

    return config;
  } catch (error) {
    console.warn(`Failed to fetch remote config for ${networkName}, using local fallback. Error: ${error}`);
    const duniterConf = (DUNITER_CONFIG as Record<string, any>)[networkName];

    if (!duniterConf) {
      throw new Error(`No local fallback configuration found for network: ${networkName}`);
    }

    return {
      dunipod: localDunipodEndpoints(networkName),
      genesisHash: duniterConf.genesisHash,
      providers: duniterConf.providers
    };
  }
}

export function getNetworkConfig (networkName: string): NetworkConfig | null {
  const configStr = localStorage.getItem(`networkConfig-${networkName}`);

  if (configStr) {
    return JSON.parse(configStr) as NetworkConfig;
  }

  return null;
}

export function loadNetworkConfig (networkName: string): Promise<NetworkConfig> {
  if (!configCache.has(networkName)) {
    configCache.set(networkName, fetchAndCache(networkName));
  }

  return configCache.get(networkName)!;
}
